import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Settings, Shield, BarChart3, Megaphone, X, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { COOKIE_DESCRIPTIONS } from '@/config/analytics';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

interface CookieConsentBannerProps {
  onAcceptAll: () => void;
  onRejectNonEssential: () => void;
  onSaveCustom: (analytics: boolean, marketing: boolean) => void;
  onDismiss?: () => void;
  className?: string;
}

export function CookieConsentBanner({
  onAcceptAll,
  onRejectNonEssential,
  onSaveCustom,
  onDismiss,
  className,
}: CookieConsentBannerProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  // Cookie consent is irrelevant inside native app WebView
  if (Capacitor.isNativePlatform()) return null;

  const handleSaveCustom = () => {
    onSaveCustom(analyticsEnabled, marketingEnabled);
  };

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className={cn(
        'fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm z-50 p-4 rounded-xl',
        'bg-background/95 backdrop-blur-lg border border-border shadow-2xl',
        className
      )}
    >
      <div className="max-w-6xl mx-auto">
        {/* Main Banner */}
        <div className="flex flex-col gap-3">
          {/* Icon & Text */}
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2 rounded-lg bg-primary/10 shrink-0">
              <Cookie className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-foreground mb-1">
                  We value your privacy
                </h3>
                {onDismiss && (
                  <button
                    type="button"
                    onClick={onDismiss}
                    aria-label="Dismiss"
                    className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 p-1 rounded"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                You can choose which cookies you'd like to accept.{' '}
                <Link 
                  to="/legal/privacy" 
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  Privacy Policy <ExternalLink className="h-3 w-3" />
                </Link>
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
              className="gap-2"
            >
              <Settings className="h-4 w-4" />
              Customize
              {showDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRejectNonEssential}
            >
              Reject All
            </Button>
            <Button
              size="sm"
              onClick={onAcceptAll}
              className="bg-primary hover:bg-primary/90"
            >
              Accept All
            </Button>
          </div>
        </div>

        {/* Expandable Details */}
        <AnimatePresence>
          {showDetails && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="pt-6 mt-6 border-t border-border">
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Essential Cookies */}
                  <div className="p-4 rounded-lg bg-muted/50 border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-green-600" />
                        <span className="font-medium">{COOKIE_DESCRIPTIONS.essential.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded">
                        Always On
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {COOKIE_DESCRIPTIONS.essential.description}
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {COOKIE_DESCRIPTIONS.essential.cookies.map((cookie) => (
                        <li key={cookie} className="flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                          {cookie}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="p-4 rounded-lg bg-muted/50 border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="h-4 w-4 text-blue-600" />
                        <span className="font-medium">{COOKIE_DESCRIPTIONS.analytics.name}</span>
                      </div>
                      <Switch
                        checked={analyticsEnabled}
                        onCheckedChange={setAnalyticsEnabled}
                        aria-label="Toggle analytics cookies"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {COOKIE_DESCRIPTIONS.analytics.description}
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {COOKIE_DESCRIPTIONS.analytics.cookies.map((cookie) => (
                        <li key={cookie} className="flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                          {cookie}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="p-4 rounded-lg bg-muted/50 border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Megaphone className="h-4 w-4 text-orange-600" />
                        <span className="font-medium">{COOKIE_DESCRIPTIONS.marketing.name}</span>
                      </div>
                      <Switch
                        checked={marketingEnabled}
                        onCheckedChange={setMarketingEnabled}
                        aria-label="Toggle marketing cookies"
                      />
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {COOKIE_DESCRIPTIONS.marketing.description}
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {COOKIE_DESCRIPTIONS.marketing.cookies.map((cookie) => (
                        <li key={cookie} className="flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                          {cookie}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Save Custom Button */}
                <div className="flex justify-end mt-4">
                  <Button onClick={handleSaveCustom} size="sm">
                    Save Preferences
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
